import { useDispatch, useSelector } from 'react-redux'
import {
  addBatchToCart,
  emptyCart,
  getPayloadProduct,
  isLoadingProduct,
} from 'redux/actions/cartActions'
import {
  getFinalPriceMdl22Service,
  getProductPriceInventoryService,
} from 'redux/services/clientsServices'
import { isWebDocumentOrigin } from 'Utils/isWebDocumentOrigin'

const findAvailableProductIndex = (products, item, usedIndexes) => {
  const matchingIndexes = products.reduce((indexes, entry, index) => {
    if (
      entry.product.ProductId?.toUpperCase() !== item.ProductId?.toUpperCase()
    ) {
      return indexes
    }
    indexes.push(index)
    return indexes
  }, [])

  return (
    matchingIndexes.find(index => !usedIndexes.has(index)) ?? matchingIndexes[0]
  )
}

// Empareja cada precio con un producto distinto del carrito: usar solo
// ProductId puede colapsar líneas duplicadas (ej. el SKU dummy de LITA)
// en un único producto si se usa `.find()` directamente.
export const matchPricesToProducts = (products, prices, warehouse) => {
  const usedIndexes = new Set()

  return prices.map(item => {
    const productIndex = findAvailableProductIndex(products, item, usedIndexes)
    if (productIndex != null) usedIndexes.add(productIndex)

    const product = productIndex != null ? products[productIndex] : undefined
    const Quantity = product?.qty || 0
    const Price = item.FinalPrice

    return {
      selectList: { ...item, Quantity, Price },
      payload: { ...product, Quantity, Price },
      warehouse,
      Quantity,
      Price,
      qty: Quantity,
    }
  })
}

export const useCheckFinalPrice = () => {
  const dispatch = useDispatch()
  const quoteHeader = useSelector(state => state.cotizacion.QuoteHeader)
  const company = useSelector(state => state.company)
  const cart = useSelector(state => state.cart.cart)

  const fethCheckFinalPrice = async (prod, customerId, newCompany) => {
    const PriceListId = quoteHeader.PriceListId
    const ProductIds = prod.map(product => product.product.ProductId)
    const WarehouseId = newCompany?.WarehouseId || company.WarehouseId
    const WarehouseCode = newCompany?.WarehouseCode || company.WarehouseCode
    const CompanyId = newCompany?.CompanyId || company.CompanyId
    const WarehouseDescription =
      newCompany?.WarehouseDescription || company.WarehouseDescription
    const finalPricePayload = {
      CompanyId,
      WarehouseId,
      ProductIds,
      ...(customerId ? { CustomerId: customerId } : { PriceListId }),
    }

    const isSaleOrderWeb = isWebDocumentOrigin(quoteHeader?.Origin)
    const response = !isSaleOrderWeb
      ? await getFinalPriceMdl22Service(finalPricePayload)
      : null

    if (!isSaleOrderWeb) {
      const { data, status } = response
      if (status === 200 && data?.Data) {
        const selectList = response.data.Data

        const warehouse = {
          WarehouseId,
          WarehouseCode,
          WarehouseDescription,
          WarehouseAddress: quoteHeader?.WarehouseAddress,
        }

        const products = matchPricesToProducts(prod, selectList, warehouse)

        return products
      }
    } else {
      const selectList = quoteHeader?.Lines

      const warehouse = {
        WarehouseId,
        WarehouseCode,
        WarehouseDescription,
        WarehouseAddress: quoteHeader?.WarehouseAddress,
      }

      const products = matchPricesToProducts(prod, selectList, warehouse)

      return products
    }
  }

  const checkFinalPrice = async ({
    prods,
    customerId,
    newCompany,
    isClone,
  } = {}) => {
    dispatch(isLoadingProduct({ isLoadingAllProducts: true }))
    const productList = prods || cart
    if (!productList.length) {
      return dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }
    const companyCode = newCompany?.CompanyCode || company.CompanyCode
    const companyId = newCompany?.CompanyId || company.CompanyId
    const WarehouseId = newCompany?.WarehouseId || company.WarehouseId

    const batches = []
    for (let i = 0; i < productList.length; i += 99) {
      batches.push(productList.slice(i, i + 99))
    }

    // Snapshot the cart before clearing so we can restore on API failure
    const cartSnapshot = productList.map(p => ({ product: p, qty: p.Quantity }))

    dispatch(emptyCart())

    try {
      // Fetch all batches in parallel instead of sequentially
      const batchResults = await Promise.all(
        batches.map(async batch => {
          const ProductIds = batch.map(product => product.ProductId)
          const combinedPayload = batch.map(prod => ({
            product: prod,
            qty: prod.Quantity,
          }))

          const priceInventoryPayload = {
            CompanyId: companyId,
            CompanyCode: companyCode,
            WarehouseId,
            ProductIds: ProductIds || [],
            UoMId: null,
            PriceListId: null,
          }

          // Both API calls for this batch run in parallel
          const [response, orderedProducts] = await Promise.all([
            getProductPriceInventoryService(priceInventoryPayload),
            fethCheckFinalPrice(combinedPayload, customerId, newCompany),
          ])

          return {
            updatedProducts: response.data?.Data || [],
            orderedProducts: orderedProducts || [],
          }
        })
      )

      const allUpdatedProducts = batchResults.flatMap(r => r.updatedProducts)
      const allOrderedProducts = batchResults.flatMap(r => r.orderedProducts)

      allOrderedProducts?.forEach((orderedProduct, index) => {
        const updatedProduct = allUpdatedProducts?.find(
          up => up.ProductId === orderedProduct.selectList.ProductId
        )
        if (updatedProduct) {
          allOrderedProducts[index].payload.product.Cost = updatedProduct.Cost
          allOrderedProducts[index].payload.product.AvgCost =
            updatedProduct.AvgCost
          allOrderedProducts[index].payload.product.AvgPrice =
            updatedProduct.AvgPrice
        }
      })

      // Build all cart items and dispatch in a single state update
      const cartItems = allOrderedProducts.map(prod => ({
        product: getPayloadProduct(
          prod.selectList,
          prod.payload,
          prod.warehouse,
          prod.Quantity,
          prod.Price,
          isClone
        ),
        qty: prod.qty,
      }))

      dispatch(addBatchToCart(cartItems))
    } catch (error) {
      // Restore the cart to its state before the API calls so the user
      // doesn't lose their products when the network request fails
      dispatch(addBatchToCart(cartSnapshot))
      throw error
    } finally {
      // Always clear the loading flag regardless of success or failure
      dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }
  }

  return { checkFinalPrice }
}
